// /admin/ads → "תהליך הרשמה": everything that happens after someone clicks an ad, in order,
// each step editable and testable. Backed by api lib/adsFlow.js (+ the existing
// register_welcome_whatsapp setting for the WhatsApp text).
import {FC, useEffect, useState} from 'react'
import FlowPreview from './FlowPreview'
import WaTemplates from './WaTemplates'
import {
  EmailStep,
  Flow,
  FlowResponse,
  getFlow,
  newLeadForm,
  saveFlow,
  saveWelcome,
  testEmail,
  testWhatsapp,
} from './_requests'

const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

const StepCard: FC<{n: number; title: string; sub?: string; children: React.ReactNode}> = ({
  n,
  title,
  sub,
  children,
}) => (
  <div className='card card-bordered mb-4'>
    <div className='card-body p-5'>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <span
          className='d-inline-flex align-items-center justify-content-center fw-bold text-white'
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            background: '#2b70fa',
            flex: '0 0 auto',
          }}
        >
          {n}
        </span>
        <div>
          <div className='fw-bold fs-5'>{title}</div>
          {sub && <div className='text-muted fs-8'>{sub}</div>}
        </div>
      </div>
      {children}
    </div>
  </div>
)

const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label className='fs-8 text-muted mb-1 d-block'>{children}</label>
)

const FlowEditor: FC = () => {
  const [data, setData] = useState<FlowResponse | null>(null)
  const [flow, setFlow] = useState<Flow | null>(null)
  const [welcome, setWelcome] = useState('')
  const [testPhone, setTestPhone] = useState('')
  const [testTo, setTestTo] = useState('')
  const [testNumbersText, setTestNumbersText] = useState('')
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState<{ok: boolean; text: string} | null>(null)

  const load = async () => {
    const {data} = await getFlow()
    setData(data)
    setFlow(data.flow)
    setWelcome(data.welcome_template)
    setTestNumbersText(
      (data.flow.whatsapp_bot?.test_numbers || [])
        .map((n) => (n.startsWith('972') ? '0' + n.slice(3) : n))
        .join(', ')
    )
  }

  useEffect(() => {
    load().catch((e) => setMsg({ok: false, text: errMsg(e)}))
  }, [])

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key)
    setMsg(null)
    try {
      setMsg({ok: true, text: await fn()})
    } catch (e) {
      setMsg({ok: false, text: errMsg(e)})
    } finally {
      setBusy('')
    }
  }

  if (!flow || !data) return <div className='text-muted'>טוען…</div>

  const set = (patch: Partial<Flow>) => setFlow({...flow, ...patch})
  const setLf = (k: keyof Flow['lead_form'], v: any) =>
    set({lead_form: {...flow.lead_form, [k]: v}})
  const setBot = (k: keyof Flow['whatsapp_bot'], v: any) =>
    set({whatsapp_bot: {...flow.whatsapp_bot, [k]: v}})
  const setStep = (i: number, patch: Partial<EmailStep>) =>
    set({
      emails: {
        ...flow.emails,
        steps: flow.emails.steps.map((s, j) => (j === i ? {...s, ...patch} : s)),
      },
    })

  const saveAll = () =>
    run('save', async () => {
      const {data: r} = await saveFlow({
        ...flow,
        whatsapp_bot: {
          ...flow.whatsapp_bot,
          test_numbers: testNumbersText.split(/[\s,]+/).filter(Boolean),
        },
      })
      setFlow(r.flow)
      setTestNumbersText(
        r.flow.whatsapp_bot.test_numbers
          .map((n) => (n.startsWith('972') ? '0' + n.slice(3) : n))
          .join(', ')
      )
      return 'נשמר'
    })

  return (
    <div className='d-flex gap-5 flex-wrap align-items-start'>
      <div style={{flex: '1 1 520px', maxWidth: 820}}>
        <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4'>
          <div className='text-muted fs-7'>
            מה קורה מהלחיצה על המודעה ועד שהלקוח מחובר. כל שלב ניתן לעריכה ולבדיקה. שינויים חלים על
            נרשמים חדשים.
          </div>
          <button className='btn btn-primary btn-sm' disabled={busy === 'save'} onClick={saveAll}>
            {busy === 'save' ? 'שומר…' : 'שמירת כל השינויים'}
          </button>
        </div>
        {msg && (
          <div className={`alert ${msg.ok ? 'alert-success' : 'alert-danger'} py-2 fs-7`}>
            {msg.text}
          </div>
        )}

        <StepCard
          n={1}
          title='לחיצה על המודעה'
          sub='לאן מודעות "דף הרשמה" מובילות, וכמה ימי ניסיון כל נרשם חדש מקבל'
        >
          <div className='row g-3'>
            <div className='col-md-8'>
              <Label>קישור ברירת מחדל למודעות</Label>
              <select
                className='form-select form-select-sm'
                dir='ltr'
                value={flow.default_link}
                onChange={(e) => set({default_link: e.target.value})}
              >
                <option value='https://console.fbzipper.com/auth/register'>
                  /auth/register — טופס הרשמה (גוגל / פייסבוק / טלפון)
                </option>
                <option value='https://console.fbzipper.com/landing'>
                  /landing — דף נחיתה עם הסבר
                </option>
                {![
                  'https://console.fbzipper.com/auth/register',
                  'https://console.fbzipper.com/landing',
                ].includes(flow.default_link) && (
                  <option value={flow.default_link}>{flow.default_link}</option>
                )}
              </select>
            </div>
            <div className='col-md-4'>
              <Label>ימי ניסיון</Label>
              <input
                type='number'
                min={1}
                max={60}
                className='form-control form-control-sm'
                value={flow.trial_days}
                onChange={(e) => set({trial_days: Number(e.target.value)})}
              />
            </div>
          </div>
          <div className='text-muted fs-8 mt-2'>
            {'{trial_days}'} בטקסטים למטה מתחלף במספר הזה. מודעות "טופס פייסבוק" לא עוברות כאן — הן
            מתחילות בשלב 2.
          </div>
        </StepCard>

        <StepCard
          n={2}
          title='טופס פייסבוק (למודעות "טופס פייסבוק")'
          sub='נפתח בתוך פייסבוק עם שם, טלפון ואימייל ממולאים מראש. כל ליד הופך אוטומטית לחשבון.'
        >
          <div className='row g-3'>
            <div className='col-md-6'>
              <Label>כותרת פתיחה</Label>
              <input
                className='form-control form-control-sm'
                value={flow.lead_form.intro_title}
                onChange={(e) => setLf('intro_title', e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <Label>כפתור פתיחה</Label>
              <input
                className='form-control form-control-sm'
                value={flow.lead_form.intro_button}
                onChange={(e) => setLf('intro_button', e.target.value)}
              />
            </div>
            <div className='col-12'>
              <Label>נקודות במסך הפתיחה (שורה לכל נקודה, עד 5)</Label>
              <textarea
                className='form-control form-control-sm'
                rows={3}
                value={flow.lead_form.intro_items.join('\n')}
                onChange={(e) => setLf('intro_items', e.target.value.split('\n'))}
              />
            </div>
            <div className='col-md-6'>
              <Label>כותרת תודה</Label>
              <input
                className='form-control form-control-sm'
                value={flow.lead_form.thanks_title}
                onChange={(e) => setLf('thanks_title', e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <Label>כפתור במסך התודה</Label>
              <input
                className='form-control form-control-sm'
                value={flow.lead_form.thanks_button}
                onChange={(e) => setLf('thanks_button', e.target.value)}
              />
            </div>
            <div className='col-12'>
              <Label>טקסט תודה</Label>
              <input
                className='form-control form-control-sm'
                value={flow.lead_form.thanks_body}
                onChange={(e) => setLf('thanks_body', e.target.value)}
              />
            </div>
          </div>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3'>
            <div className='text-muted fs-8'>
              {data.lead_form.form_id
                ? `טופס נוכחי: ${data.lead_form.form_id}${
                    data.lead_form.form_created_at
                      ? ' · ' + new Date(data.lead_form.form_created_at).toLocaleString('he-IL')
                      : ''
                  }`
                : 'עוד לא נוצר טופס — הוא נוצר אוטומטית במודעת הטופס הראשונה.'}{' '}
              פייסבוק לא מאפשר לערוך טופס קיים: אחרי שמירה לוחצים "צור טופס חדש" והמודעות הבאות
              ישתמשו בו.
              {!data.leads_poll_on && (
                <span className='text-danger'>
                  {' '}
                  · משיכת לידים כבויה בשרת (חסרה הרשאת leads_retrieval).
                </span>
              )}
            </div>
            <button
              className='btn btn-sm btn-light-primary'
              disabled={busy === 'form'}
              onClick={() =>
                run('form', async () => {
                  await saveFlow(flow)
                  const {data: r} = await newLeadForm()
                  await load()
                  return `נוצר טופס חדש: ${r.form_id}`
                })
              }
            >
              {busy === 'form' ? 'יוצר…' : 'שמור וצור טופס חדש'}
            </button>
          </div>
        </StepCard>

        <StepCard
          n={3}
          title='בוט וואטסאפ (054-615-5952)'
          sub='מי שכותב למספר — ממודעת "פתיחת וואטסאפ" או ישירות — מקבל מענה אוטומטי, נותן שם ותחום, ומקבל חשבון ניסיון וקישור כניסה באותו צ׳אט'
        >
          <div className='form-check form-switch mb-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='wa-bot-on'
              checked={flow.whatsapp_bot.enabled}
              onChange={(e) => setBot('enabled', e.target.checked)}
            />
            <label className='form-check-label fs-7' htmlFor='wa-bot-on'>
              הבוט פעיל (כשכבוי — ההודעות רק נכנסות לתיבה בלשונית וואטסאפ)
            </label>
          </div>
          <div className='d-flex align-items-center gap-2 mb-3 fs-7'>
            <span>הפסקה בין הודעות רצופות של הבוט:</span>
            <input
              type='number'
              min={0}
              max={30}
              className='form-control form-control-sm'
              style={{width: 70}}
              value={flow.whatsapp_bot.step_delay_sec}
              onChange={(e) => setBot('step_delay_sec', Number(e.target.value))}
            />
            <span>שניות (לפני צעד 2: פי 3)</span>
          </div>
          <div className='form-check form-switch mb-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='wa-assistant-on'
              checked={flow.whatsapp_bot.assistant_enabled !== false}
              onChange={(e) => setBot('assistant_enabled', e.target.checked)}
            />
            <label className='form-check-label fs-7' htmlFor='wa-assistant-on'>
              עוזר AI ללקוחות רשומים (אחרי ההרשמה): עונה ומבצע — קמפיינים, פוסטים, קבוצות, נתוני
              היום, חשבוניות, תשלומים, קישור תשלום, ניסיון, דוח PDF. עובר לנציג בבקשות החזר/ביטול/תלונה,
              ושותק 12 שעות אחרי שנציג עונה מהתיבה.
            </label>
          </div>
          <Label>1. הודעת פתיחה (שואלת לשם) · תגיות: {'{trial_days}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={4}
            value={flow.whatsapp_bot.greeting}
            onChange={(e) => setBot('greeting', e.target.value)}
          />
          <Label>2. שאלה על התחום · תגיות: {'{first_name}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={2}
            value={flow.whatsapp_bot.ask_business}
            onChange={(e) => setBot('ask_business', e.target.value)}
          />
          <Label>2ב. לנדל״ן בלבד (AI מזהה): שאלת ערים · תגיות: {'{first_name}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={2}
            value={flow.whatsapp_bot.ask_cities}
            onChange={(e) => setBot('ask_cities', e.target.value)}
          />
          <Label>אחרי שנבנו הקמפיינים · תגיות: {'{count} {campaigns} {first_name}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={2}
            value={flow.whatsapp_bot.campaigns_created}
            onChange={(e) => setBot('campaigns_created', e.target.value)}
          />
          <Label>
            3. החשבון נפתח (בלי פרטי כניסה) · תגיות: {'{first_name} {trial_days}'}
          </Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={7}
            value={flow.whatsapp_bot.created}
            onChange={(e) => setBot('created', e.target.value)}
          />
          <Label>מי שכבר לקוח (פעם ביום לכל היותר) · תגיות: {'{first_name} {login_url}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={3}
            value={flow.whatsapp_bot.existing}
            onChange={(e) => setBot('existing', e.target.value)}
          />

          <div className='fw-bold fs-7 mb-2'>אחרי שהחשבון נפתח — שני צעדים, אחד-אחד:</div>
          <Label>
            4. צעד 1 — סרטון mp4 (עד 16MB) אופציונלי; ריק = נשלח רק הטקסט עם כפתור הכניסה לחשבון
          </Label>
          <input
            className='form-control form-control-sm mb-2'
            dir='ltr'
            value={flow.whatsapp_bot.tutorial_video}
            onChange={(e) => setBot('tutorial_video', e.target.value)}
          />
          <Label>
            צעד 1 — כניסה לחשבון (שם מחכה המדריך המדורג) · תגיות: {'{first_name} {login_url}'}
          </Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={2}
            value={flow.whatsapp_bot.tutorial_caption}
            onChange={(e) => setBot('tutorial_caption', e.target.value)}
          />
          <div className='form-check form-switch mb-2'>
            <input
              className='form-check-input'
              type='checkbox'
              id='wa-connect-step-on'
              checked={flow.whatsapp_bot.connect_step_enabled === true}
              onChange={(e) => setBot('connect_step_enabled', e.target.checked)}
            />
            <label className='form-check-label fs-7' htmlFor='wa-connect-step-on'>
              5. לשלוח גם הודעת "חיבור פרופיל פייסבוק" נפרדת (כבוי = רק כניסה לחשבון; המדריך באתר
              מחבר את הפייסבוק בצעד 3)
            </label>
          </div>
          <Label>סרטון "חיבור פרופיל פייסבוק" (קישור mp4, ריק = רק טקסט)</Label>
          <input
            className='form-control form-control-sm mb-2'
            dir='ltr'
            value={flow.whatsapp_bot.connect_video}
            onChange={(e) => setBot('connect_video', e.target.value)}
          />
          <Label>
            הוראות + קישור החיבור · תגיות: {'{connect_url} {first_name}'} (עד 1024 תווים)
          </Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={8}
            value={flow.whatsapp_bot.connect_caption}
            onChange={(e) => setBot('connect_caption', e.target.value)}
          />
          <Label>
            6. כשהפרופיל מתחבר — פרטי הכניסה (סיסמה חדשה נוצרת ברגע הזה) · תגיות:{' '}
            {'{first_name} {login_url} {email} {password}'}
          </Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={7}
            value={flow.whatsapp_bot.logged_in}
            onChange={(e) => setBot('logged_in', e.target.value)}
          />
          <Label>7. מיד אחרי פרטי הכניסה — סרטון ההדרכה המלא (קישור mp4, ריק = לא נשלח)</Label>
          <input
            className='form-control form-control-sm mb-2'
            dir='ltr'
            value={flow.whatsapp_bot.full_tutorial_video || ''}
            onChange={(e) => setBot('full_tutorial_video', e.target.value)}
          />
          <Label>כיתוב לסרטון · תגיות: {'{first_name}'}</Label>
          <textarea
            className='form-control form-control-sm mb-3'
            rows={2}
            value={flow.whatsapp_bot.full_tutorial_caption || ''}
            onChange={(e) => setBot('full_tutorial_caption', e.target.value)}
          />

          <div
            className='rounded p-4'
            style={{background: '#f8fafc', border: '1px dashed #cbd5e1'}}
          >
            <div className='fw-bold fs-7 mb-2'>🧪 בדיקה עם מספר אחר</div>
            <div className='row g-2'>
              <div className='col-md-8'>
                <Label>מספרי בדיקה (מופרדים בפסיק)</Label>
                <input
                  className='form-control form-control-sm'
                  dir='ltr'
                  placeholder='050-1234567, 052-7654321'
                  value={testNumbersText}
                  onChange={(e) => setTestNumbersText(e.target.value)}
                />
              </div>
              <div className='col-md-4'>
                <Label>פקודת איפוס</Label>
                <input
                  className='form-control form-control-sm'
                  dir='ltr'
                  value={flow.whatsapp_bot.reset_command}
                  onChange={(e) => setBot('reset_command', e.target.value)}
                />
              </div>
            </div>
            <ol className='fs-8 text-muted mt-3 mb-0 ps-4'>
              <li>
                מוסיפים כאן את מספר הבדיקה ולוחצים "שמירת כל השינויים". מספר בדיקה עובר את כל התהליך
                כלקוח חדש גם אם יש לו כבר חשבון (החשבון הקיים לא נוגעים בו).
              </li>
              <li>
                מהטלפון הזה שולחים כל הודעה ל-<b>054-615-5952</b> — הבוט עונה ומתחיל את התהליך.
              </li>
              <li>
                כדי להתחיל מההתחלה שולחים <b dir='ltr'>clear</b> (או{' '}
                <b dir='ltr'>{flow.whatsapp_bot.reset_command || '#reset'}</b>) — השיחה נמחקת, וחשבון
                שהבוט פתח למספר הזה מושבת (רק חשבון שהבוט פתח, ורק ממספרי בדיקה).
              </li>
            </ol>
          </div>
        </StepCard>

        <WaTemplates />

        <StepCard
          n={4}
          title='וואטסאפ ברוכים הבאים'
          sub='נשלח מיד בהרשמה עם טלפון ולכל ליד מטופס; בהרשמת גוגל/פייסבוק — אחרי שהלקוח מזין טלפון'
        >
          <Label>תגיות: {'{first_name} {email} {password} {login_url}'}</Label>
          <textarea
            className='form-control form-control-sm'
            rows={12}
            value={welcome}
            onChange={(e) => setWelcome(e.target.value)}
          />
          <div className='d-flex gap-2 flex-wrap mt-3'>
            <button
              className='btn btn-sm btn-primary'
              disabled={busy === 'welcome'}
              onClick={() =>
                run('welcome', async () => {
                  await saveWelcome(welcome)
                  return 'הודעת הוואטסאפ נשמרה'
                })
              }
            >
              שמירת ההודעה
            </button>
            <button
              className='btn btn-sm btn-light'
              onClick={() => setWelcome(data.welcome_default)}
            >
              החזר לברירת מחדל
            </button>
            <div className='d-flex gap-2 ms-auto'>
              <input
                className='form-control form-control-sm'
                dir='ltr'
                style={{width: 160}}
                placeholder='050-0000000'
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
              />
              <button
                className='btn btn-sm btn-light-success text-nowrap'
                disabled={busy === 'wa' || !testPhone}
                onClick={() =>
                  run('wa', async () => {
                    const {data: r} = await testWhatsapp(testPhone, welcome)
                    return `הודעת בדיקה נשלחה ל-${r.to}`
                  })
                }
              >
                {busy === 'wa' ? 'שולח…' : 'שלח בדיקה'}
              </button>
            </div>
          </div>
        </StepCard>

        <StepCard
          n={5}
          title='מיילי תזכורת לחיבור פרופיל'
          sub='רק למי שעוד לא חיבר פרופיל פייסבוק. נעצרים ברגע שהוא מחובר או מסיר את עצמו.'
        >
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                id='emails-on'
                checked={flow.emails.enabled}
                onChange={(e) => set({emails: {...flow.emails, enabled: e.target.checked}})}
              />
              <label className='form-check-label fs-7' htmlFor='emails-on'>
                שליחת מיילים פעילה
                {!data.reminders_cron_on && <span className='text-danger'> (כבוי בשרת הזה)</span>}
              </label>
            </div>
            <div className='d-flex gap-2'>
              <input
                className='form-control form-control-sm'
                dir='ltr'
                style={{width: 220}}
                placeholder='אימייל לבדיקות'
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
            </div>
          </div>
          <div className='text-muted fs-8 mb-3'>
            תגיות: {'{first_name} {trial_days}'} · כל מייל כולל כפתור כניסה בלחיצה אחת וקישור הסרה.
          </div>

          {flow.emails.steps.map((s, i) => (
            <div
              key={i}
              className='border rounded p-4 mb-3'
              style={{opacity: s.enabled ? 1 : 0.55}}
            >
              <div className='d-flex align-items-center gap-3 flex-wrap mb-3'>
                <b>מייל {i + 1}</b>
                <div className='d-flex align-items-center gap-2'>
                  <span className='fs-8 text-muted'>נשלח</span>
                  <input
                    type='number'
                    min={1}
                    className='form-control form-control-sm'
                    style={{width: 80}}
                    value={s.after_hours}
                    onChange={(e) => setStep(i, {after_hours: Number(e.target.value)})}
                  />
                  <span className='fs-8 text-muted'>
                    שעות אחרי ההרשמה
                    {s.after_hours >= 24
                      ? ` (≈ ${Math.round((s.after_hours / 24) * 10) / 10} ימים)`
                      : ''}
                  </span>
                </div>
                <div className='form-check form-switch ms-auto'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={s.enabled}
                    onChange={(e) => setStep(i, {enabled: e.target.checked})}
                  />
                </div>
                <button
                  className='btn btn-sm btn-light-success'
                  disabled={busy === `mail${i}` || !testTo}
                  onClick={() =>
                    run(`mail${i}`, async () => {
                      const {data: r} = await testEmail(testTo, i, s)
                      return `מייל בדיקה ${i + 1} נשלח ל-${r.to}`
                    })
                  }
                >
                  {busy === `mail${i}` ? 'שולח…' : 'שלח בדיקה'}
                </button>
                <button
                  className='btn btn-sm btn-icon btn-light-danger'
                  title='מחיקת המייל'
                  onClick={() =>
                    set({
                      emails: {...flow.emails, steps: flow.emails.steps.filter((_, j) => j !== i)},
                    })
                  }
                >
                  <i className='bi bi-trash' />
                </button>
              </div>
              <div className='row g-2'>
                <div className='col-md-6'>
                  <Label>נושא</Label>
                  <input
                    className='form-control form-control-sm'
                    value={s.subject}
                    onChange={(e) => setStep(i, {subject: e.target.value})}
                  />
                </div>
                <div className='col-md-6'>
                  <Label>כותרת</Label>
                  <input
                    className='form-control form-control-sm'
                    value={s.title}
                    onChange={(e) => setStep(i, {title: e.target.value})}
                  />
                </div>
                <div className='col-12'>
                  <Label>תוכן</Label>
                  <textarea
                    className='form-control form-control-sm'
                    rows={3}
                    value={s.body}
                    onChange={(e) => setStep(i, {body: e.target.value})}
                  />
                </div>
                <div className='col-md-6'>
                  <Label>טקסט הכפתור</Label>
                  <input
                    className='form-control form-control-sm'
                    value={s.cta}
                    onChange={(e) => setStep(i, {cta: e.target.value})}
                  />
                </div>
              </div>
            </div>
          ))}
          <div className='d-flex gap-2'>
            <button
              className='btn btn-sm btn-light'
              onClick={() =>
                set({
                  emails: {
                    ...flow.emails,
                    steps: [
                      ...flow.emails.steps,
                      {
                        enabled: true,
                        after_hours: 120,
                        subject: '',
                        title: '',
                        body: '',
                        cta: 'לחיבור הפרופיל',
                      },
                    ],
                  },
                })
              }
            >
              + הוספת מייל
            </button>
            <button
              className='btn btn-sm btn-light'
              onClick={() => set({emails: data.defaults.emails})}
            >
              החזר מיילים לברירת מחדל
            </button>
          </div>
          <div className='text-muted fs-8 mt-3'>
            מחיקה או שינוי סדר של מיילים משנים את המספור: מי שכבר קיבל את "מייל 2" לא יקבל שוב את מה
            שנמצא עכשיו במקום 2.
          </div>
        </StepCard>

        <StepCard
          n={6}
          title='לקוח מחובר'
          sub='ברגע שפרופיל פייסבוק מחובר — המיילים נעצרים והלקוח מופיע כ"חיבר פרופיל" בטבלאות בעמוד המודעות'
        >
          <div className='text-muted fs-7'>
            אין מה להגדיר כאן. מעקב: לשונית "מודעות" → נרשמים ממודעות.
          </div>
        </StepCard>

        <div className='text-end'>
          <button className='btn btn-primary' disabled={busy === 'save'} onClick={saveAll}>
            {busy === 'save' ? 'שומר…' : 'שמירת כל השינויים'}
          </button>
        </div>
      </div>

      {/* Live preview of every message, per path — follows unsaved edits. */}
      <div
        className='card card-bordered'
        style={{
          flex: '1 1 420px',
          position: 'sticky',
          top: 16,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
        }}
      >
        <div className='card-body p-5'>
          <div className='fw-bold fs-5 mb-1'>👁 מה הלקוח יקבל</div>
          <div className='text-muted fs-8 mb-4'>
            כל ההודעות לפי הסדר, עם ערכים לדוגמה. מתעדכן תוך כדי עריכה (גם לפני שמירה).
          </div>
          <FlowPreview flow={flow} welcome={welcome} />
        </div>
      </div>
    </div>
  )
}

export default FlowEditor
